import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  Subject,
  tap,
  catchError,
  throwError,
  firstValueFrom,
  fromEvent,
  merge,
  timer,
} from 'rxjs';
import { throttleTime, takeUntil, retry } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment.dev';
import { LoggerService } from './logger.service';
import {
  LoginRequest,
  LoginResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ApiResponse,
} from '../models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<LoginResponse | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private refreshTokenTimeout?: any;

  // ── Idle Logout ─────────────────────────────────────────────
  // Per-tab only. Each tab tracks its own inactivity independently.
  // Idle logout NEVER broadcasts to other tabs — a tab that is idle
  // logs itself out silently. Other tabs with activity keep running.
  //private readonly IDLE_TIMEOUT_MS = 1 * 60 * 1000; // 1 min (testing)
  private readonly IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 min (production)
  private idleTimeout?: any;
  private stopIdle$ = new Subject<void>();

  // ── BroadcastChannel — cross-tab coordination ──────────────
  // Three message types:
  //   { type: 'token_refreshed', expiresAt: string }
  //     → Sent by whichever tab successfully refreshed.
  //       All other tabs cancel their pending refresh timers and
  //       reschedule based on the new expiry. They never call the API.
  //
  //   { type: 'logout' }
  //     → Sent ONLY on explicit user logout (sidebar button).
  //       Idle logout does NOT send this — it is tab-scoped.
  //
  //   { type: 'leader_died', expiresAt: string }
  //     → FIX #1: Sent by a tab on beforeunload if it holds the leader lock.
  //       All other tabs reschedule themselves so one of them becomes the
  //       new leader when the timer fires. Without this, non-leader tabs
  //       have no timer running and the token expires unrefreshed → logout.
  private readonly channel = typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('auth_sync')
    : null;

  // ── Refresh leader lock (localStorage) ──────────────────────
  // PROBLEM: Tab A and Tab B both open. Both call initialize() → both
  // schedule a refresh timer for the same expiry. Both timers fire at
  // the same moment. Tab A hits the backend first → backend ROTATES the
  // refresh token cookie. Tab B arrives milliseconds later with the OLD
  // cookie → 401 → both tabs log out. This is the "invalid token" bug.
  //
  // SOLUTION — Leader election via localStorage:
  //   Before calling the API, a tab atomically writes its own unique ID
  //   as the leader. Any other tab that fires checks the leader key and
  //   sees it is NOT the leader → skips the API call entirely.
  //   The winning tab broadcasts 'token_refreshed' → all other tabs
  //   reschedule their timers. Only one HTTP request ever reaches the backend.
  //
  // Why localStorage instead of BroadcastChannel for the lock?
  //   BroadcastChannel messages are async — by the time Tab B receives
  //   "Tab A is refreshing", Tab B has already sent its own request.
  //   localStorage reads are synchronous — Tab B checks before sending.
  private readonly LEADER_KEY = 'se_refresh_leader';   // which tab owns the refresh
  private readonly LEADER_TTL = 15_000;                 // 15 s — clears if tab crashes mid-refresh
  private readonly tabId      = `tab_${Math.random().toString(36).slice(2)}`; // unique per tab instance

  // ── FIX #1: Track last known expiry so we can reschedule if leader dies ──
  // When electLeader() returns false (another tab is leader), we still store
  // the expiry and schedule a WATCHDOG timer. If the leader tab closes without
  // broadcasting 'token_refreshed', the watchdog fires and this tab takes over.
  private lastKnownExpiresAt?: string | Date;

  constructor(
    private http: HttpClient,
    private router: Router,
    private logger: LoggerService,
    private ngZone: NgZone,
  ) {
    this.listenToCrossTabMessages();

    // ── FIX #1: Release leader lock and notify other tabs on tab close ──
    // Without this, if the leader tab closes mid-refresh:
    //   - The LEADER_KEY stays in localStorage for up to LEADER_TTL (15s)
    //   - Non-leader tabs see a stale lock and skip their refresh
    //   - Token expires → 401 → logoutLocal() → unexpected logout
    window.addEventListener('beforeunload', () => {
      const isLeader = this.isCurrentTabLeader();
      this.releaseLeader();

      // Tell other tabs the leader is gone so they can reschedule immediately.
      // Include lastKnownExpiresAt so they set the correct timer duration.
      if (isLeader && this.lastKnownExpiresAt) {
        this.channel?.postMessage({
          type:      'leader_died',
          expiresAt: this.lastKnownExpiresAt,
        });
      }
    });
  }

  // ============================================================
  // Cross-tab message listener
  // ============================================================
  private listenToCrossTabMessages(): void {
    if (!this.channel) return;

    this.channel.onmessage = (event) => {
      const msg = event.data as { type: string; expiresAt?: string };

      switch (msg.type) {

        // Another tab refreshed the token successfully.
        // Cancel our pending refresh and reschedule based on the new expiry.
        // We do NOT call the API — the cookie is already updated by the winning tab.
        case 'token_refreshed':
          this.logger.info('Token refreshed by another tab — rescheduling timer');
          this.ngZone.run(() => this.startRefreshTokenTimer(msg.expiresAt));
          break;

        // Explicit user logout from another tab — log out here too.
        // This is NOT triggered by idle logout (idle is tab-scoped).
        case 'logout':
          this.ngZone.run(() => {
            this.logger.info('Logout broadcast received — clearing session');
            this.stopRefreshTokenTimer();
            this.stopIdleTimer();
            this.currentUserSubject.next(null);
            this.router.navigate(['/auth/login']);
          });
          break;

        // ── FIX #1: Leader tab closed — reschedule our own timer ──
        // The leader tab is gone. We reschedule ourselves so one of the
        // remaining tabs will become the new leader when the timer fires.
        // Without this message, non-leader tabs have NO timer running
        // and the token silently expires → 401 on the next API call → logout.
        case 'leader_died':
          this.logger.info('Leader tab closed — rescheduling refresh timer');
          this.ngZone.run(() => this.startRefreshTokenTimer(msg.expiresAt));
          break;
      }
    };
  }

  // ============================================================
  // Idle Logout Timer  —  PER-TAB, never cross-tab
  // ============================================================
  private startIdleTimer(): void {
    // stopIdleTimer() completes the old stopIdle$ and allocates a fresh one.
    // This terminates any existing activity$ subscription before creating a new one,
    // preventing duplicate event listeners when startIdleTimer() is called twice
    // (once in initialize() on page load, once again in login()).
    this.stopIdleTimer();

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'mousedown'),
        fromEvent(window, 'touchstart'),
        fromEvent(window, 'scroll'),
        fromEvent(window, 'click'),
        // FIX: also reset idle when the tab regains focus — user may have
        // been active in another app and just switched back
        fromEvent(window, 'focus'),
      ).pipe(
        throttleTime(500),
        takeUntil(this.stopIdle$),
      );

      // Each activity event resets only THIS tab's idle countdown.
      // No broadcast — Tab B's idle is Tab B's own business.
      activity$.subscribe(() => this.resetIdleTimer());

      this.resetIdleTimer(); // start the initial countdown
    });
  }

  private resetIdleTimer(): void {
    clearTimeout(this.idleTimeout);
    this.idleTimeout = setTimeout(() => this.onIdle(), this.IDLE_TIMEOUT_MS);
  }

  private onIdle(): void {
    this.ngZone.run(() => {
      this.logger.info('This tab idle — logging out tab only');
      this.stopIdleTimer();
      // CRITICAL: call logoutThisTabOnly(), NOT logout().
      // logout() broadcasts 'logout' and hits the server — that would kill ALL tabs.
      // We only want to clear this tab's local state and redirect to login.
      this.logoutThisTabOnly();
    });
  }

  private stopIdleTimer(): void {
    clearTimeout(this.idleTimeout);
    this.stopIdle$.next();
    this.stopIdle$.complete();
    this.stopIdle$ = new Subject<void>();
  }

  // ============================================================
  // Initialize — called by APP_INITIALIZER
  // ============================================================
  async initialize(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any>>(`${this.apiUrl}/auth/me`, {
          withCredentials: true,
        }),
      );

      if (response.success && response.data) {
        this.logger.info('Session restored');
        this.currentUserSubject.next({
          requirePasswordChange: false,
          accessToken: '',
          refreshToken: '',
          ...response.data,
        });
        this.startRefreshTokenTimer(response.data.accessTokenExpiresAt);
        this.startIdleTimer();
      }
    } catch {
      this.logger.info('No active session');
      this.currentUserSubject.next(null);
    }
  }

  // ============================================================
  // Login
  // ============================================================
  login(credentials: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.http
      .post<ApiResponse<LoginResponse>>(
        `${this.apiUrl}/auth/login`,
        credentials,
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.logger.info('Login successful');
            this.currentUserSubject.next(response.data);
            this.startRefreshTokenTimer(response.data.accessTokenExpiresAt);
            this.startIdleTimer();
          }
        }),
        catchError((error) => {
          this.logger.error('Login failed', error);
          return throwError(() => error);
        }),
      );
  }

  // ============================================================
  // Refresh Token  —  called only by the elected leader tab
  // ============================================================
  refreshToken(): Observable<ApiResponse<LoginResponse>> {
    return this.http
      .post<ApiResponse<LoginResponse>>(
        `${this.apiUrl}/auth/refresh-token`,
        {},
        { withCredentials: true },
      )
      .pipe(
        // ── FIX #2: Retry up to 2 times on network errors before giving up ──
        // Previously ANY error (wifi blip, 500, timeout) called logoutLocal()
        // immediately. Now we retry twice with a 1s then 2s delay.
        // We only give up and logout if all 3 attempts fail, OR if the server
        // explicitly returns 401/403 (refresh token truly expired/invalid).
        retry({
          count: 2,
          delay: (error: HttpErrorResponse, retryCount: number) => {
            // Don't retry on 401/403 — the refresh token is genuinely invalid.
            // Retrying would just hammer the server and delay the logout.
            if (error.status === 401 || error.status === 403) {
              throw error;
            }
            const delayMs = retryCount * 1000; // 1s, then 2s
            this.logger.warn(`Token refresh attempt ${retryCount} failed (${error.status}) — retrying in ${delayMs}ms`);
            return timer(delayMs);
          },
        }),
        tap((response) => {
          if (response.success && response.data) {
            this.logger.info('Token refreshed successfully');

            // Merge — refresh endpoint only returns token fields, not role/schoolName.
            // Full replace would lose role → guard redirects to /unauthorized.
            const existing = this.currentUserSubject.value;
            this.currentUserSubject.next({ ...existing, ...response.data });

            // Reschedule this tab's own timer first.
            this.startRefreshTokenTimer(response.data.accessTokenExpiresAt);

            // Then tell all other tabs the new expiry.
            // They will reschedule their timers and will NOT call the API.
            this.channel?.postMessage({
              type:      'token_refreshed',
              expiresAt: response.data.accessTokenExpiresAt,
            });
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('Token refresh failed after retries', error);
          this.releaseLeader(); // release lock so next attempt can elect a new leader

          if (error.status === 401 || error.status === 403) {
            // Refresh token is genuinely invalid/expired — session is dead.
            // Logout is correct here. No point retrying.
            this.logger.warn('Refresh token rejected by server (401/403) — logging out');
            this.logoutLocal();
          } else {
            // Network error (status 0), server error (5xx), or timeout.
            // The user's session may still be perfectly valid — the server
            // just didn't respond. Do NOT logout.
            // Schedule a retry in 30s. If that also fails, it will retry again.
            // Only a genuine 401/403 from the server will ever trigger logout.
            this.logger.warn(`Refresh failed with status ${error.status} — retrying in 30s, session preserved`);
            setTimeout(() => {
              this.ngZone.run(() => this.startRefreshTokenTimer(this.lastKnownExpiresAt));
            }, 30_000);
          }

          return throwError(() => error);
        }),
      );
  }

  // ============================================================
  // Auto-Refresh Timer  —  Leader Election
  // ============================================================

  // ── Leader helpers ────────────────────────────────────────
  // Leader record stored in localStorage:
  //   { tabId: string, claimedAt: number }
  // A tab is the leader if:
  //   (a) no leader record exists, OR
  //   (b) the existing record belongs to THIS tab, OR
  //   (c) the existing record is older than LEADER_TTL (the previous leader crashed)

  private electLeader(): boolean {
    try {
      const raw = localStorage.getItem(this.LEADER_KEY);
      if (raw) {
        const record = JSON.parse(raw) as { tabId: string; claimedAt: number };
        const age = Date.now() - record.claimedAt;

        if (record.tabId !== this.tabId && age < this.LEADER_TTL) {
          // Another tab holds a fresh lock — it will refresh. We stand down.
          this.logger.info(`Tab ${record.tabId} is refresh leader — skipping`);
          return false;
        }
      }

      // No leader, stale leader, or we are already the leader — claim it.
      localStorage.setItem(this.LEADER_KEY, JSON.stringify({
        tabId:     this.tabId,
        claimedAt: Date.now(),
      }));
      return true;
    } catch {
      // localStorage unavailable (private mode edge case) — allow refresh
      return true;
    }
  }

  // ── FIX #1: Helper to check if THIS tab currently holds the leader lock ──
  // Used in beforeunload to decide whether to broadcast 'leader_died'.
  private isCurrentTabLeader(): boolean {
    try {
      const raw = localStorage.getItem(this.LEADER_KEY);
      if (!raw) return false;
      const record = JSON.parse(raw) as { tabId: string };
      return record.tabId === this.tabId;
    } catch {
      return false;
    }
  }

  private releaseLeader(): void {
    try {
      const raw = localStorage.getItem(this.LEADER_KEY);
      if (!raw) return;
      const record = JSON.parse(raw) as { tabId: string };
      // Only release our own lock — never clear another tab's lock.
      if (record.tabId === this.tabId) {
        localStorage.removeItem(this.LEADER_KEY);
      }
    } catch { /* ignore */ }
  }

  // ── Timer ─────────────────────────────────────────────────
  private startRefreshTokenTimer(expiresAt?: string | Date): void {
    this.stopRefreshTokenTimer();

    // ── FIX #1: Always store the latest expiry so beforeunload can broadcast it ──
    if (expiresAt) {
      this.lastKnownExpiresAt = expiresAt;
    }

    // Refresh 60 s before expiry. Falls back to 55 min if no expiry provided.
    let msUntilRefresh = 55 * 60 * 1000;

    if (expiresAt) {
      const expiryMs = new Date(expiresAt).getTime();
      msUntilRefresh = Math.max(expiryMs - Date.now() - 60_000, 5_000);
    }

    this.logger.info(`Refresh scheduled in ${Math.round(msUntilRefresh / 1000)}s (tab: ${this.tabId})`);

    this.refreshTokenTimeout = setTimeout(() => {
      // ── FIX #1: Non-leader tabs no longer silently return and die ──
      // OLD: if (!this.electLeader()) return;
      //   → Tab became leaderless if the actual leader tab closed before
      //     broadcasting 'token_refreshed'. No timer was running on this tab.
      //     Token expired → 401 on next API call → logoutLocal() → logout.
      //
      // NEW: If we lose the election, schedule a WATCHDOG timer.
      //   The watchdog fires slightly after LEADER_TTL (the max time a leader
      //   can hold the lock). If the leader tab refreshed successfully, it
      //   will broadcast 'token_refreshed' and this watchdog will be cancelled
      //   by startRefreshTokenTimer() being called again from the broadcast handler.
      //   If the leader tab died silently (no broadcast), the watchdog fires,
      //   this tab wins the next election, and performs the refresh itself.
      if (!this.electLeader()) {
        const watchdogMs = this.LEADER_TTL + 2_000; // slight buffer after TTL
        this.logger.info(`Not leader — watchdog set for ${watchdogMs}ms in case leader dies`);
        this.refreshTokenTimeout = setTimeout(() => {
          this.logger.info('Watchdog fired — attempting to take over refresh');
          this.startRefreshTokenTimer(this.lastKnownExpiresAt);
        }, watchdogMs);
        return;
      }

      this.logger.info(`This tab is leader — refreshing token`);

      this.refreshToken().subscribe({
        next:  () => this.releaseLeader(),
        error: () => {}, // releaseLeader + logoutLocal already called in catchError
      });
    }, msUntilRefresh);
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) clearTimeout(this.refreshTokenTimeout);
  }

  // ============================================================
  // Logout variants
  // ============================================================

  // Full logout — explicit user action (sidebar button).
  // Broadcasts to all tabs. Calls the backend to revoke the refresh token.
  logout(returnUrl?: string): void {
    this.logger.info('User logout initiated');
    this.stopRefreshTokenTimer();
    this.stopIdleTimer();
    this.releaseLeader();

    // Clear state before the HTTP call so LoginComponent never sees a stale user.
    this.currentUserSubject.next(null);

    if (returnUrl && returnUrl !== '/auth/login') {
      sessionStorage.setItem('postLoginRedirect', returnUrl);
    }

    // Only broadcast on explicit logout — NOT on idle logout.
    this.channel?.postMessage({ type: 'logout' });

    this.http
      .post(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true })
      .subscribe({
        complete: () => this.router.navigate(['/auth/login']),
        error:    () => this.router.navigate(['/auth/login']),
      });
  }

  // Tab-only idle logout — clears THIS tab's local state only.
  // NO server call  — cookie stays alive so other tabs keep working.
  // NO broadcast    — other tabs are completely unaffected.
  //
  // What happens when user comes back to this tab:
  //   APP_INITIALIZER calls /auth/me → cookie still valid →
  //   session silently restored → user redirected to dashboard.
  //   No password required.
  //
  // Real session security = backend cookie expiry (8–12 hrs)
  //                       + HttpOnly + Secure cookie flags.
  // The idle timer is a screen lock, not a security boundary.
  private logoutThisTabOnly(): void {
    this.logger.info('Idle logout — this tab only, session preserved on server');
    this.stopRefreshTokenTimer();
    this.stopIdleTimer();
    this.releaseLeader();
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  // Local state clear — used by interceptor after refresh failure.
  // No broadcast, no server call (server already rejected the token).
  logoutLocal(): void {
    this.logger.info('Clearing local session');
    this.stopRefreshTokenTimer();
    this.stopIdleTimer();
    this.releaseLeader();
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  // ============================================================
  // Password Management
  // ============================================================
  forgotPassword(request: ForgotPasswordRequest): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/auth/forgot-password`, request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/auth/reset-password`, request);
  }

  changePassword(data: any): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      `${this.apiUrl}/auth/change-password`, data, { withCredentials: true });
  }

  // ============================================================
  // Get Current User
  // ============================================================
  getCurrentUser(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/auth/me`, { withCredentials: true });
  }

  // ============================================================
  // Helper Methods
  // ============================================================
  isAuthenticated(): boolean             { return !!this.currentUserValue; }
  get currentUserValue()                 { return this.currentUserSubject.value; }
  updateCurrentUser(user: LoginResponse) { this.currentUserSubject.next(user); }
  getUserRole(): string | null           { return this.currentUserValue?.role || null; }
  isSuperAdmin(): boolean                { return this.getUserRole() === 'SuperAdmin'; }
  isAdmin(): boolean                     { const r = this.getUserRole(); return r === 'Admin' || r === 'SuperAdmin'; }
  isTeacher(): boolean                   { return this.getUserRole() === 'Teacher'; }
  isStaff(): boolean                     { return this.getUserRole() === 'NonTeachingStaff'; }
  isStudent(): boolean                   { return this.getUserRole() === 'Student'; }
  hasRole(role: string): boolean         { return this.getUserRole() === role; }

  hasAnyRole(roles: string[]): boolean {
    const userRole = this.getUserRole();
    if (userRole === 'SuperAdmin') return true;
    return userRole ? roles.includes(userRole) : false;
  }

  getUserId(): string | null     { return this.currentUserValue?.userId?.toString() || null; }
  getSchoolId(): string | null   { return this.currentUserValue?.schoolId?.toString() || null; }
  getUserEmail(): string | null  { return this.currentUserValue?.email || null; }
  getSchoolName(): string | null { return this.currentUserValue?.schoolName || null; }

  getUserName(): string | null {
    const email = this.getUserEmail();
    return email ? email.split('@')[0] : null;
  }

  getUserDisplayName(): string | null {
    return this.getUserEmail();
  }
}